import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

function subscribeMedia(query: string) {
  return (cb: () => void) => {
    const mq = window.matchMedia(query);
    mq.addEventListener('change', cb);
    return () => mq.removeEventListener('change', cb);
  };
}

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribeMedia(query),
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** 767px 以下 = モバイル（ボトムタブ・ボトムシート） */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

/** 1023px 以下 = サイドバーを畳む */
export function useIsCompact(): boolean {
  return useMediaQuery('(max-width: 1023px)');
}

export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

/**
 * 文字サイズ設定（Dynamic Type）の検出。ブラウザの既定フォントサイズが 131% 以上（≒ 21px）なら 'large'。
 * <html data-font-scale="large"> を付け、タブバーのラベルを消す・ラベルを値の上に積む（05 §3.3b）。
 */
export function useFontScale(): 'normal' | 'large' {
  const read = useCallback(() => {
    const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return px >= 16 * 1.31 ? ('large' as const) : ('normal' as const);
  }, []);
  const [scale, setScale] = useState<'normal' | 'large'>(() => (typeof document === 'undefined' ? 'normal' : read()));
  useEffect(() => {
    const update = () => setScale(read());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [read]);
  useEffect(() => {
    document.documentElement.dataset.fontScale = scale;
  }, [scale]);
  return scale;
}

/** navigator.onLine（C-13 オフラインバナー用） */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
    () => true,
  );
}

/** localStorage に保存する状態（読めない環境では初期値のまま） */
export function useLocalStorage<T extends string>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      return (localStorage.getItem(key) as T | null) ?? initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setV(next);
      try {
        localStorage.setItem(key, next);
      } catch {
        /* ignore */
      }
    },
    [key],
  );
  return [v, set];
}
